import React from 'react';
import {
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Toolbar,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  CardMembership as LicenseIcon,
  Assignment as AssignmentIcon,
  Cloud as CloudIcon,
  MailOutline as MailIcon,
  Contacts as ContactsIcon,
  Email as MailingListIcon,
  PersonAdd as OnboardingIcon,
  PersonRemove as OffboardingIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';

const drawerWidth = 240;

const microsoft365MenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: ROUTES.DASHBOARD },
  { text: 'Users', icon: <PeopleIcon />, path: ROUTES.USERS },
  { text: 'Groups', icon: <GroupIcon />, path: ROUTES.GROUPS },
  { text: 'Licenses', icon: <LicenseIcon />, path: ROUTES.LICENSES },
  { text: 'Licence Assignment', icon: <AssignmentIcon />, path: ROUTES.LICENSE_ASSIGNMENT },
  { text: 'Azure Permissions', icon: <CloudIcon />, path: ROUTES.AZURE_PERMISSIONS },
  { text: 'Shared Mailboxes', icon: <MailIcon />, path: ROUTES.SHARED_MAILBOXES },
  { text: 'Contacts', icon: <ContactsIcon />, path: ROUTES.CONTACTS },
  { text: 'Mailing Lists', icon: <MailingListIcon />, path: ROUTES.MAILING_LISTS },
  { text: 'Onboarding', icon: <OnboardingIcon />, path: ROUTES.ONBOARDING },
  { text: 'Offboarding', icon: <OffboardingIcon />, path: ROUTES.OFFBOARDING },
];

const categoryLicenseMenuItems = [
  { text: 'Licenses', icon: <LicenseIcon />, path: ROUTES.GITHUB_LICENSES },
  { text: 'Users', icon: <PeopleIcon />, path: ROUTES.TEAMVIEWER_LICENSES },
  { text: 'Channels', icon: <CloudIcon />, path: ROUTES.TEAMVIEWER_CHANNELS },
  { text: 'Licenses', icon: <LicenseIcon />, path: ROUTES.AUTOCAD_LICENSES },
  { text: 'Licenses', icon: <LicenseIcon />, path: ROUTES.TRIPLETEX_LICENSES },
  { text: 'Licenses', icon: <LicenseIcon />, path: ROUTES.TELARIS_LICENSES },
  { text: 'Licenses', icon: <LicenseIcon />, path: ROUTES.ONSHAPE_LICENSES },
];

const otherPlatformGroups = [
  {
    title: 'GitHub',
    items: [categoryLicenseMenuItems[0]],
  },
  {
    title: 'TeamViewer',
    items: [categoryLicenseMenuItems[1], categoryLicenseMenuItems[2]],
  },
  {
    title: 'AutoCAD',
    items: [categoryLicenseMenuItems[3]],
  },
  {
    title: 'Tripletex',
    items: [categoryLicenseMenuItems[4]],
  },
  {
    title: 'Telaris',
    items: [categoryLicenseMenuItems[5]],
  },
  {
    title: 'OnShape',
    items: [categoryLicenseMenuItems[6]],
  },
];

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isMicrosoft365Open, setIsMicrosoft365Open] = React.useState(false);
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(() =>
    otherPlatformGroups.reduce(
      (acc, group) => {
        acc[group.title] = false;
        return acc;
      },
      {} as Record<string, boolean>
    )
  );

  const isRouteInGroup = (items: { path: string }[]) =>
    items.some((item) => location.pathname === item.path);

  const renderMenuItems = (items: { text: string; icon: React.ReactNode; path: string }[]) =>
    items.map((item) => (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          selected={location.pathname === item.path}
          onClick={() => navigate(item.path)}
          sx={{ pl: 4 }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItemButton>
      </ListItem>
    ));

  const toggleGroup = (groupTitle: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar />
      <List sx={{ pt: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setIsMicrosoft365Open((prev) => !prev)}
            selected={isRouteInGroup(microsoft365MenuItems)}
          >
            <ListItemText primary={<Typography variant="subtitle1">Microsoft 365</Typography>} />
            {isMicrosoft365Open ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={isMicrosoft365Open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {renderMenuItems(microsoft365MenuItems)}
          </List>
        </Collapse>

        {otherPlatformGroups.map((group) => (
          <React.Fragment key={group.title}>
            <ListItem disablePadding sx={{ mt: 1 }}>
              <ListItemButton
                onClick={() => toggleGroup(group.title)}
                selected={isRouteInGroup(group.items)}
              >
                <ListItemText
                  primary={<Typography variant="subtitle1">{group.title}</Typography>}
                />
                {openGroups[group.title] ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openGroups[group.title]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(group.items)}
              </List>
            </Collapse>
          </React.Fragment>
        ))}

        <ListItem disablePadding sx={{ mt: 2 }}>
          <ListItemButton
            selected={location.pathname === ROUTES.SETTINGS}
            onClick={() => navigate(ROUTES.SETTINGS)}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary={<Typography variant="subtitle1">Settings</Typography>} />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};
